<template>
    <main-header />
    <sub-header
        heading="Checkout"
        subHeading="Complete your order by filling the form below"
    />
    <section>
        <div class="container">
            <form class="billing-info" @submit.prevent="placeOrder">
                <!-- Left stepper -->
                <div ref="stepper" class="stepper" :style="{ paddingTop: stepperOffset + 'px' }">
                    <div class="step" :class="{ current: step === 1, done: step > 1 }">
                        <span>01</span>
                    </div>
                    <div class="line" :class="{ active: step > 1 }" :style="{ height: lineHeights[0] + 'px' }"></div>
                    <div class="step" :class="{ current: step === 2, done: step > 2 }">
                        <span>02</span>
                    </div>
                    <div class="line" :class="{ active: step > 2 }" :style="{ height: lineHeights[1] + 'px' }"></div>
                    <div class="step" :class="{ current: step === 3, done: step > 3 }">
                        <span>03</span>
                    </div>
                    <div class="line" :class="{ active: step > 3 }" :style="{ height: lineHeights[2] + 'px' }"></div>
                    <div class="step" :class="{ current: step === 4 }">
                        <span>04</span>
                    </div>
                </div>

                <!-- Center content -->
                <div class="form">
                    <!-- Shipping Address -->
                    <div class="form-section" :class="{ active: step === 1 }" ref="section1">
                        <div class="section-header" @click="toggleSection(1)">
                            <div class="section-header-left">
                                <div class="section-title-row">
                                    <h4>Shipping Address</h4>
                                </div>
                                <div v-if="step !== 1 && hasBilling" class="section-preview">
                                    <div class="preview-name">{{ firstName }} {{ lastName }}</div>
                                    <div class="preview-address">{{ formattedAddress }}</div>
                                </div>
                            </div>
                            <button type="button" class="edit-btn" @click.stop="toggleSection(1)">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10z"/>
                                </svg>
                                <span>Edit</span>
                            </button>
                        </div>
                        <div
                            class="collapsible-content"
                            :class="{ active: step === 1 }"
                            :style="step === 1 ? { maxHeight: '2200px' } : { maxHeight: '0' }"
                        >
                            <div class="form-group">
                                <div class="form-control">
                                    <label>First Name *</label>
                                    <input type="text" required v-model="firstName" />
                                </div>
                                <div class="form-control">
                                    <label>Last Name *</label>
                                    <input type="text" required v-model="lastName" />
                                </div>
                            </div>
                            
                            <div class="form-control">
                                <label>Country *</label>
                                <input type="text" required v-model="userCountry" />
                            </div>
                            <div class="form-control">
                                <label>Street Address *</label>
                                <input
                                    type="text"
                                    required
                                    v-model="userStreetAddress"
                                />
                            </div>
                            <div class="form-group">
                                <div class="form-control">
                                    <label>Town / City *</label>
                                    <input type="text" required v-model="userCity" />
                                </div>
                                <div class="form-control">
                                    <label>State / County *</label>
                                    <input type="text" required v-model="userState" />
                                </div>
                                <div class="form-control">
                                    <label>Zip Code *</label>
                                    <input type="text" v-model="mailingZip" required />
                                </div>
                            </div>
                            <div class="form-control">
                                <label>Email Address *</label>
                                <input type="email" required v-model="userEmail" />
                            </div>
                            <div class="form-control">
                                <label>Order Notes (optional)</label>
                                <textarea
                                    rows="10"
                                    placeholder="Notes about your order, e.g. special notes for delivery"
                                    v-model="orderNotes"
                                ></textarea>
                            </div>
                            <button type="button" class="submit" @click="saveShippingAndNext">Continue to Shipping</button>
                        </div>
                    </div>

                    <!-- Shipping Method -->
                    <div class="form-section" :class="{ active: step === 2 }" ref="section2">
                        <div class="section-header" @click="toggleSection(2)">
                            <div class="section-header-left">
                                <div class="section-title-row">
                                    <h4>Shipping Method</h4>
                                </div>
                                <div v-if="step !== 2" class="section-preview">
                                    <div class="preview-method">{{ shippingMethodDisplay }}</div>
                                </div>
                            </div>
                            <button type="button" class="edit-btn" @click.stop="toggleSection(2)">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10z"/>
                                </svg>
                                <span>Edit</span>
                            </button>
                        </div>
                        <div
                            class="collapsible-content"
                            :class="{ active: step === 2 }"
                            :style="step === 2 ? { maxHeight: '2400px' } : { maxHeight: '0' }"
                        >
                            <div class="shipping-methods">
                                <label class="shipping-option" :class="{ selected: shippingMethodLocal === 'standard' }">
                                    <div class="shipping-option-header">
                                        <input type="radio" value="standard" v-model="shippingMethodLocal" />
                                        <div class="shipping-title-row">
                                            <span class="shipping-name">YunExpress Standard</span>
                                            <span class="shipping-price">
                                                <template v-if="standardIsFree">
                                                    FREE
                                                </template>
                                                <template v-else>
                                                    $ {{ calculatedStandardRate.toFixed(2) }}
                                                </template>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="shipping-description">
                                        <div class="shipping-info-row">
                                            <svg class="info-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                                            </svg>
                                            <span class="shipping-time">7-14 days</span>
                                        </div>
                                        <div class="shipping-info-row">
                                            <svg class="info-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                                            </svg>
                                            <span class="shipping-time">Production: 5-8 days</span>
                                        </div>
                                    </div>
                                </label>
                                
                                <label class="shipping-option" :class="{ selected: shippingMethodLocal === 'express' }">
                                    <div class="shipping-option-header">
                                        <input type="radio" value="express" v-model="shippingMethodLocal" />
                                        <div class="shipping-title-row">
                                            <span class="shipping-name">YunExpress Express</span>
                                            <span class="shipping-price">$ {{ calculatedExpressRate.toFixed(2) }}</span>
                                        </div>
                                    </div>
                                    <div class="shipping-description">
                                        <div class="shipping-info-row">
                                            <svg class="info-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                                            </svg>
                                            <span class="shipping-time">3-5 days</span>
                                        </div>
                                        <div class="shipping-info-row">
                                            <svg class="info-icon" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                                            </svg>
                                            <span class="shipping-time">Production: 5-8 days</span>
                                        </div>
                                    </div>
                                </label>

                                <div class="powered-by-yunexpress">
                                    <span>Powered by YunExpress</span>
                                </div>
                            </div>

                            <p class="free-shipping-notice" v-if="standardIsFree">
                                <strong>Great news!</strong> Standard shipping is <strong>FREE</strong> on orders over
                                ${{ freeShippingThreshold.toFixed(2) }}. Your order already qualifies.
                            </p>
                            <p class="free-shipping-notice" v-else>
                                Add <strong>${{ amountToFreeShipping.toFixed(2) }}</strong> more to get
                                <strong>FREE standard shipping</strong> on orders over
                                ${{ freeShippingThreshold.toFixed(2) }}.
                            </p>

                            <div class="button-container">
                                <button type="button" class="submit" @click="saveMethodAndNext">Continue to Review</button>
                            </div>
                        </div>
                    </div>

                    <!-- Review & Place Order -->
                    <div class="form-section" :class="{ active: step === 3 }" ref="section3">
                        <div class="section-header" @click="toggleSection(3)">
                            <div class="section-header-left">
                                <div class="section-title-row">
                                    <h4>Review & Place Order</h4>
                                </div>
                                <div v-if="step !== 3 && cart && cart.length" class="section-preview">
                                    <div class="preview-name">
                                        {{ cart.length === 1 ? '1 item' : cart.length + ' items' }}
                                    </div>
                                    <div class="preview-address">
                                        Total ${{ orderTotal.toFixed(2) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="collapsible-content"
                            :class="{ active: step === 3 }"
                            :style="step === 3 ? { maxHeight: '4000px' } : { maxHeight: '0' }"
                        >
                            <div class="review-products">
                                <h5>Products</h5>
                                <div class="cart-header-row">
                                    <span class="col-product">Product Name</span>
                                    <span class="col-qty">Quantity</span>
                                    <span class="col-price">Price</span>
                                </div>
                                <div class="divider"></div>
                                <div v-for="(item, index) in cart" :key="index" class="cart-row">
                                    <div class="product-cell">
                                        <img :src="getCartItemImage(item)" alt="product" class="thumb" />
                                        <div class="product-meta">
                                            <div class="product-title">{{ item.brand || item.name }}</div>
                                            <div class="chip in-stock">In Stock</div>
                                        </div>
                                    </div>
                                    <div class="qty-cell">
                                        <div class="qty-control">
                                            <button type="button" class="qty-btn" @click="changeQuantity(item, -1)">−</button>
                                            <span class="qty-value">{{ item.quantity || 1 }}</span>
                                            <button type="button" class="qty-btn" @click="changeQuantity(item, 1)">+</button>
                                        </div>
                                    </div>
                                    <div class="price-cell">
                                        $ {{ ((item.price || 0) * (item.quantity || 1)).toFixed(2) }}
                                    </div>
                                    <button type="button" class="trash" @click="deleteItem(index)">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            
                            <div class="order-summary-inline">
                                <h5>Order Summary</h5>
                                <div class="summary-row">
                                    <span class="label">Subtotal:</span>
                                    <span class="value">$ {{ adjustedSubtotal.toFixed(2) }}</span>
                                </div>
                                <div class="summary-row" v-if="portraitStyleFee > 0">
                                    <span class="label">Portrait Style Add-on:</span>
                                    <span class="value">$ {{ portraitStyleFee.toFixed(2) }}</span>
                                </div>
                                <div class="summary-row" v-if="couponDiscount > 0">
                                    <span class="label">Coupon Discount:</span>
                                    <span class="value discount">- $ {{ couponDiscount.toFixed(2) }}</span>
                                </div>
                                <div class="summary-row" v-if="firstPurchaseDiscount > 0">
                                    <span class="label">First Purchase (10%):</span>
                                    <span class="value discount">- $ {{ firstPurchaseDiscount.toFixed(2) }}</span>
                                </div>
                                <div class="summary-row">
                                    <span class="label">Shipping:</span>
                                    <span class="value">
                                        <template v-if="shippingFee === 0">
                                            FREE
                                        </template>
                                        <template v-else>
                                            $ {{ shippingFee.toFixed(2) }}
                                        </template>
                                    </span>
                                </div>
                                <div class="divider"></div>
                                <div class="summary-row total">
                                    <span class="label">Total:</span>
                                    <span class="value">$ {{ orderTotal.toFixed(2) }}</span>
                                </div>
                            </div>
                            
                            <button type="button" class="submit submit-continue" @click="goToNextStep(4)">Continue to Payment</button>
                        </div>
                    </div>

                    <!-- Payment -->
                    <div class="form-section" :class="{ active: step === 4 }" ref="section4">
                        <div class="section-header" @click="toggleSection(4)">
                            <div class="section-header-left">
                                <div class="section-title-row">
                                    <h4>Payment</h4>
                                </div>
                            </div>
                        </div>
                        <div
                            class="collapsible-content"
                            :class="{ active: step === 4 }"
                            :style="step === 4 ? { maxHeight: '2400px' } : { maxHeight: '0' }"
                        >
                            <div class="payment-method-header">
                                <div class="payment-title-row">
                                    <h5>Payment Information</h5>
                                    <div class="trust-badges">
                                        <span class="trust-badge">SSL Encrypted</span>
                                        <span class="trust-badge">✓ PCI Compliant</span>
                                    </div>
                                </div>
                                <p class="payment-description">
                                    Your payment is processed securely by <strong>Stripe</strong>, trusted by millions of businesses worldwide. 
                                    You'll be redirected to Stripe's secure checkout page to complete your payment.
                                </p>
                                <div class="accepted-cards-row">
                                    <div class="cards-label">Accepted Payment Methods:</div>
                                    <div class="card-logos">
                                        <span class="card-logo visa">VISA</span>
                                        <span class="card-logo mastercard">Mastercard</span>
                                        <span class="card-logo amex">AMEX</span>
                                        <span class="card-logo discover">Discover</span>
                                        <span class="card-logo">+more</span>
                                    </div>
                                </div>
                            </div>
                            
                            <p v-if="paymentError" class="payment-error">
                                <span class="error-icon">⚠️</span>
                                {{ paymentError }}
                            </p>
                            
                            <div class="security-footer">
                                <div class="stripe-powered">
                                    <span class="powered-text">Powered by</span>
                                    <svg width="50" height="21" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 01-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 013.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 01-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 01-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 00-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fill="#635BFF"/>
                                    </svg>
                                </div>
                                <div class="security-features">
                                    <div class="security-item">
                                        <svg class="security-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                        </svg>
                                        <span class="security-label">Secure Checkout</span>
                                    </div>
                          
                                </div>
                            </div>
                            <button type="button" class="submit submit-pay" @click="placeOrder" :disabled="!noOrder">
                                <span v-if="noOrder">Pay Now with Stripe</span>
                                <span v-else>Processing...</span>
                            </button>
                        </div>
                    </div>

                </div>
                <!-- Right column: Apply Coupon + Order Summary (combined) -->
                <aside>
                    <div class="card card-summary">
                        <h4>Apply Coupon</h4>
                        <div class="coupon-input">
                            <input type="text" v-model="couponCode" placeholder="Enter coupon code" />
                            <button type="button" class="apply" @click="applyCoupon">APPLY</button>
                        </div>
                        <div class="divider"></div>
                        <h4 class="summary-title">Order Summary</h4>
                        <div class="summary-row">
                            <span class="label">Subtotal</span>
                            <span class="value">$ {{ adjustedSubtotal.toFixed(2) }}</span>
                        </div>
                        <div class="summary-row" v-if="portraitStyleFee > 0">
                            <span class="label">Portrait Style Add-on</span>
                            <span class="value">$ {{ portraitStyleFee.toFixed(2) }}</span>
                        </div>
                        <div class="summary-row" v-if="couponDiscount > 0">
                            <span class="label">Coupon Discount</span>
                            <span class="value">- $ {{ couponDiscount.toFixed(2) }}</span>
                        </div>
                        <div class="summary-row" v-if="firstPurchaseDiscount > 0">
                            <span class="label">New Customer Offer</span>
                            <span class="value">- $ {{ firstPurchaseDiscount.toFixed(2) }}</span>
                        </div>
                        <div class="summary-row">
                            <span class="label">Delivery Fee</span>
                            <span class="value">
                                <template v-if="standardIsFree && shippingMethodLocal === 'standard'">
                                    FREE
                                </template>
                                <template v-else>
                                    $ {{ shippingFee.toFixed(2) }}
                                </template>
                            </span>
                        </div>
                        <div class="divider"></div>
                        <div class="summary-row total">
                            <span class="label">Total</span>
                            <span class="value">$ {{ orderTotal.toFixed(2) }}</span>
                        </div>
                    </div>
                </aside>
            </form>
        </div>
    </section>
    <success-modal
        @close-modal="closeModal"
        :price="this.price"
        :email="this.userEmail"
        :reference="referenceMessage"
        :orderNum="this.orderNumber"
        v-if="showModal"
    />
    <main-footer />
</template>

<script>
import MainHeader from "@/components/MainHeader.vue";
import MainFooter from "@/components/MainFooter.vue";
import ButtonPreloader from "@/components/preloaders/ButtonPreloader.vue";
import SubHeader from "@/components/SubHeader.vue";
import SuccessModal from "@/components/SuccessModal.vue";

import { mapState, mapGetters } from "vuex";
import axios from "axios";

// Firebase
import { onAuthStateChanged } from "firebase/auth"
import { app, auth, db, functions, httpsCallable} from "@/firebase/init";
import { collection, addDoc, onSnapshot } from "firebase/firestore";

// Firebase Stripe Payments
import { getStripePayments, createCheckoutSession } from "@invertase/firestore-stripe-payments";
import { loadStripe } from "@stripe/stripe-js";
 
export default {
    data() {
        return {
            step: 1,
            noOrder: true,
            firstName: "",
            lastName: "",
            userCountry: "",
            userState: "",
            userCity: "",
            userStreetAddress: "",
            postal_code: "",
                    // Mailing address
                    mailingFirstName: "",
                    mailingLastName: "",
                    mailingCountry: "",
                    mailingStreetAddress: "",
                    mailingCity: "",
                    mailingState: "",
                    mailingZip: "",
            phone_number: "",
            referenceMessage: "",
            price: "",
            total: "",
            showModal: false,
            userEmail: "",
            orderNotes: "",
            userDetails: {
                displayName: null,
                email: null,
            },
            orderPaid: false,
            stripe: null,
            stripePayments: null,
            orderId: null,
            orderNumber: null,
            loading: false,
            showMailingAddress: false,
            loggedInEmail: "",
            // Summary + coupon
            couponCode: "",
            couponDiscount: 0,
            shippingMethodLocal: 'standard',
            paymentError: '',
            isFirstPurchase: false,
            // Stepper connector heights (between steps 1-2, 2-3, 3-4)
            lineHeights: [70, 70, 70],
            stepperOffset: 28,
            _lineHeightTimer: null,
            _resizeHandler: null,
            // Shipping (temporarily hardcoded during proof of use)
            calculatedStandardRate: 18.00,
            calculatedExpressRate: 30.00,
            freeShippingThreshold: 49.00,
            selectedCountryCode: '',
            _scrollTimer: null,
        };
    },
    components: {
        MainHeader,
        MainFooter,
        ButtonPreloader,
        SubHeader,
        SuccessModal,
    },
    computed: {
        ...mapState(["user", "cart", "journeys"]),
        ...mapGetters(["subtotal", "getJourneyByProductId"]),
        portraitStyleFee() {
            if (!Array.isArray(this.cart)) return 0;
            return this.cart.reduce((sum, item) => {
                const perUnitPortraits = this.getPortraitStyleCountForCartItem(item);
                if (!perUnitPortraits) return sum;
                const qty = item?.quantity || 1;
                return sum + perUnitPortraits * qty * 5;
            }, 0);
        },
        adjustedSubtotal() {
            return this.subtotal + this.portraitStyleFee;
        },
        firstPurchaseDiscount() {
            return this.isFirstPurchase ? this.adjustedSubtotal * 0.10 : 0;
        },
        effectiveSubtotal() {
            return this.adjustedSubtotal - this.couponDiscount - this.firstPurchaseDiscount;
        },
        shippingFee() {
            // Flat rates: $18 standard, $30 express, but standard is free above threshold
            if (this.shippingMethodLocal === 'standard' && this.effectiveSubtotal >= this.freeShippingThreshold) {
                return 0;
            }

            return this.shippingMethodLocal === 'express'
                ? this.calculatedExpressRate
                : this.calculatedStandardRate;
        },
        orderTotal() {
            const total = this.effectiveSubtotal + this.shippingFee;
            return total < 0 ? 0 : total;
        },
        shippingMethodSummary() {
            return this.shippingMethodLocal === 'express' ? 'Express shipping' : 'Standard shipping';
        },
        shippingMethodDisplay() {
            const method = this.shippingMethodLocal === 'express' ? 'Express' : 'Standard';
            const isStandard = this.shippingMethodLocal === 'standard';
            const isFreeStandard = isStandard && this.effectiveSubtotal >= this.freeShippingThreshold;
            const price = isStandard ? this.calculatedStandardRate : this.calculatedExpressRate;

            if (isFreeStandard) {
                return `${method} Shipping (FREE)`;
            }

            return `${method} Shipping - $${price.toFixed(2)}`;
        },
        standardIsFree() {
            return this.effectiveSubtotal >= this.freeShippingThreshold;
        },
        amountToFreeShipping() {
            const remaining = this.freeShippingThreshold - this.effectiveSubtotal;
            return remaining > 0 ? remaining : 0;
        },
        formattedAddress() {
            const parts = [];
            if (this.userStreetAddress) parts.push(this.userStreetAddress);
            const cityState = [this.userCity, this.userState].filter(Boolean).join(', ');
            if (cityState) parts.push(cityState);
            if (this.mailingZip) parts.push(this.mailingZip);
            if (this.userCountry) parts.push(this.userCountry);
            return parts.join(' • ');
        },
        hasBilling() {
            return (
                this.firstName &&
                this.lastName &&
                this.userCountry &&
                this.userState &&
                this.userCity &&
                this.userStreetAddress &&
                this.userEmail
            );
        },
        hasMailing() { return false; },
        paymentReady() {
            return !!this.stripe && !!this.stripePayments;
        },
        billingSummary() {
            if (!this.hasBilling) return "";
            const name = `${this.firstName} ${this.lastName}`.trim();
            const addr = [this.userStreetAddress, this.userCity, this.userState, this.userCountry]
                .filter(Boolean)
                .join(", ");
            return `${name} — ${addr}`;
        },
        mailingSummary() { return ""; },
        paymentSummary() {
            return this.paymentReady ? "Payment details ready" : "Payment not set";
        },
        billingSummaryDetailed() {
            if (!this.hasBilling) return "Incomplete billing information";
            return [
                `${this.firstName} ${this.lastName}`,
                this.userEmail,
                this.userStreetAddress,
                `${this.userCity}, ${this.userState}`,
                this.userCountry,
            ]
                .filter(Boolean)
                .join(" | ");
        },
        mailingSummaryDetailed() { return ""; },
    },
    methods: {
        getJourneyForCartItem(item) {
            if (!item) return null;
            const productId = item.productId || item._id;
            if (!productId) return null;
            if (typeof this.getJourneyByProductId === 'function') {
                const journey = this.getJourneyByProductId(productId);
                if (journey) return journey;
            }
            return this.journeys ? this.journeys[productId] : null;
        },
        getPortraitStyleCountForCartItem(item) {
            if (!item) return 0;
            const journey = this.getJourneyForCartItem(item);
            
            // Priority 1: Use the pre-calculated count from journey metadata (persisted in localStorage)
            if (journey && typeof journey.portraitStyleCount === 'number') {
                return journey.portraitStyleCount;
            }

            // Priority 2: Try to calculate from detailed product data
            const journeyProducts = this.getJourneyProducts(journey);
            const directProducts = this.getJourneyProducts(item);
            const products = journeyProducts.length ? journeyProducts : directProducts;
            if (products.length) {
                return products.reduce(
                    (count, product) => count + (this.productHasPortraitStyle(product) ? 1 : 0),
                    0
                );
            }
            
            // Priority 3: Check item-level flag
            return item.portraitStyle ? 1 : 0;
        },
        getJourneyProducts(source) {
            if (!source || typeof source !== 'object') return [];
            if (Array.isArray(source.setProducts) && source.setProducts.length) {
                return source.setProducts;
            }
            if (Array.isArray(source.fullControlSets) && source.fullControlSets.length) {
                return source.fullControlSets;
            }
            return [];
        },
        productHasPortraitStyle(product) {
            if (!product || typeof product !== 'object') return false;
            if (product.portraitStyle) return true;
            if (product.userSelections?.portraitStyle) return true;
            if (Array.isArray(product.personalization)) {
                return product.personalization.some((entry) =>
                    this.personalizationEntryHasPortraitStyle(entry)
                );
            }
            return false;
        },
        personalizationEntryHasPortraitStyle(entry) {
            if (!entry || !entry.input) return false;
            const input = entry.input;
            if (Array.isArray(input)) {
                return input.some((inputItem) => !!inputItem?.portraitStyle);
            }
            if (typeof input === 'object') {
                if (input.portraitStyle) return true;
            }
            return false;
        },
        // reuse CartView actions inline
        deleteItem(index) {
            this.$store.dispatch('delete_item', index);
        },
        changeQuantity(item, delta) {
            const next = Math.max(1, Math.min(10, (item.quantity || 1) + delta));
            if (next === item.quantity) return;
            this.$store.dispatch('update_quantity', { _id: item._id, size: item.size, quantity: next });
        },
        getCartItemImage(item) {
            // Prefer uploaded image URLs from personalization flows
            if (Array.isArray(item.uploadedImageUrls) && item.uploadedImageUrls.length > 0) {
                return item.uploadedImageUrls[0];
            }

            // Then use generic imageUrls field
            if (Array.isArray(item.imageUrls) && item.imageUrls.length > 0) {
                return item.imageUrls[0];
            }

            // Finally fall back to images array (can be string URLs or objects with url)
            if (Array.isArray(item.images) && item.images.length > 0) {
                const first = item.images[0];
                if (typeof first === 'string') {
                    return first;
                }
                if (first && typeof first === 'object' && first.url) {
                    return first.url;
                }
            }

            return '';
        },
        toggleSection(section) {
            this.step = this.step === section ? 0 : section;
            this.updateLinesSmoothly();
            this.scrollToSection(section);
        },
        goToNextStep(nextStep) {
            this.step = nextStep;
            this.updateLinesSmoothly();
            this.scrollToSection(nextStep);
        },
        scrollToSection(section) {
            // Smooth scroll to the section, accounting for sticky header + sub-header
            this.$nextTick(() => {
                if (typeof window === 'undefined') return;

                // Cancel any pending scroll from a previous step change
                if (this._scrollTimer) {
                    window.clearTimeout(this._scrollTimer);
                    this._scrollTimer = null;
                }

                // Wait for collapse/expand animation (~600ms) to finish
                this._scrollTimer = window.setTimeout(() => {
                    const sectionRef = this.$refs[`section${section}`];
                    if (!sectionRef || !sectionRef.getBoundingClientRect) return;

                    const rect = sectionRef.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

                    // Estimate total fixed header height (main header + nav)
                    let headerOffset = 0;

                    const headerEl = document.querySelector('header');
                    if (headerEl && headerEl.offsetHeight) {
                        headerOffset += headerEl.offsetHeight;
                    }

                    // Extra margin so the card is not glued to the header
                    const extraMargin = 8;

                    const targetY = Math.max(rect.top + scrollTop - headerOffset - extraMargin, 0);

                    try {
                        // Instant jump to final position to avoid perceived double scroll
                        window.scrollTo({ top: targetY, behavior: 'auto' });
                    } catch (e) {
                        // Fallback for older browsers
                        window.scrollTo(0, targetY);
                    }

                    this._scrollTimer = null;
                }, 650); // match collapsible-content transition duration
            });
        },
        onAddressChange(addressData) {
            // Map country code to full name
            const countryMap = {
                'US': 'United States',
                'CA': 'Canada',
                'GB': 'United Kingdom',
                'AU': 'Australia',
                'DE': 'Germany',
                'FR': 'France',
                'IT': 'Italy',
                'ES': 'Spain',
                'NL': 'Netherlands',
                'CN': 'China',
                'JP': 'Japan',
                'KR': 'South Korea',
                'SG': 'Singapore',
                'MX': 'Mexico',
                'BR': 'Brazil',
            };
            
            this.selectedCountryCode = addressData.country;
            this.userCountry = countryMap[addressData.country] || addressData.country;
            this.userStreetAddress = addressData.street;
            this.userCity = addressData.city;
            this.userState = addressData.state;
            this.mailingZip = addressData.zip;
            
            console.log('📍 Address updated:', {
                country: this.userCountry,
                street: this.userStreetAddress,
                city: this.userCity,
                state: this.userState,
                zip: this.mailingZip,
            });
        },
        saveShippingAndNext() {
            // Save shipping address (validation removed - using hardcoded shipping)
            this.$store.dispatch('set_shipping_address', {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.userEmail,
                phone: this.phone_number,
                country: this.userCountry,
                state: this.userState,
                city: this.userCity,
                street: this.userStreetAddress,
                zip: this.mailingZip,
            });
            
            // Save to localStorage in case user cancels payment
            this.saveCheckoutState();
            this.goToNextStep(2);
        },
        saveMethodAndNext() {
            this.$store.dispatch('set_shipping_method', this.shippingMethodLocal);
            // Save to localStorage in case user cancels payment
            this.saveCheckoutState();
            this.goToNextStep(3);
        },
        saveCheckoutState() {
            // Save checkout form data to localStorage
            const checkoutState = {
                firstName: this.firstName,
                lastName: this.lastName,
                userEmail: this.userEmail,
                phone_number: this.phone_number,
                userCountry: this.userCountry,
                userState: this.userState,
                userCity: this.userCity,
                userStreetAddress: this.userStreetAddress,
                mailingZip: this.mailingZip,
                orderNotes: this.orderNotes,
                shippingMethodLocal: this.shippingMethodLocal,
                couponCode: this.couponCode,
                couponDiscount: this.couponDiscount,
                step: this.step,
                savedAt: Date.now(),
            };
            localStorage.setItem('checkout_state', JSON.stringify(checkoutState));
            console.log('💾 Checkout state saved to localStorage');
        },
        restoreCheckoutState() {
            // Restore checkout form data from localStorage
            try {
                const saved = localStorage.getItem('checkout_state');
                if (!saved) return false;
                
                const checkoutState = JSON.parse(saved);
                
                // Only restore if saved within last 24 hours
                const hoursSinceSave = (Date.now() - checkoutState.savedAt) / (1000 * 60 * 60);
                if (hoursSinceSave > 24) {
                    localStorage.removeItem('checkout_state');
                    return false;
                }
                
                // Restore all form fields
                this.firstName = checkoutState.firstName || '';
                this.lastName = checkoutState.lastName || '';
                this.userEmail = checkoutState.userEmail || '';
                this.phone_number = checkoutState.phone_number || '';
                this.userCountry = checkoutState.userCountry || '';
                this.userState = checkoutState.userState || '';
                this.userCity = checkoutState.userCity || '';
                this.userStreetAddress = checkoutState.userStreetAddress || '';
                this.mailingZip = checkoutState.mailingZip || '';
                this.orderNotes = checkoutState.orderNotes || '';
                this.shippingMethodLocal = checkoutState.shippingMethodLocal || 'standard';
                this.couponCode = checkoutState.couponCode || '';
                this.couponDiscount = checkoutState.couponDiscount || 0;
                this.step = checkoutState.step || 1;
                
                console.log('✅ Checkout state restored from localStorage');
                return true;
            } catch (error) {
                console.error('Error restoring checkout state:', error);
                return false;
            }
        },
        clearCheckoutState() {
            localStorage.removeItem('checkout_state');
            console.log('🗑️ Checkout state cleared from localStorage');
        },
        applyCoupon() {
            // Simple demo rule: code OXY10 gives 10% off
            const code = (this.couponCode || '').trim().toUpperCase();
            if (!code) {
                this.couponDiscount = 0;
                return;
            }
            if (code === 'OXY10') {
                this.couponDiscount = Number((this.adjustedSubtotal * 0.1).toFixed(2));
            } else if (code === 'OXY40') {
                // mimic figma discount magnitude example
                this.couponDiscount = Math.min(this.adjustedSubtotal, 40);
            } else {
                this.couponDiscount = 0;
            }
        },
        async placeOrder() {
            console.log("🔵 [FIREBASE STRIPE] Place Order clicked");
            console.log("🔵 [FIREBASE STRIPE] Cart items:", this.cart.length);
            console.log("🔵 [FIREBASE STRIPE] Order total: $", this.orderTotal);
            
            this.noOrder = false;
            this.paymentError = '';
            
            try {
                // Ensure user is authenticated
                if (!auth.currentUser) {
                    throw new Error("You must be logged in to place an order");
                }

                if (!this.paymentReady) {
                    throw new Error("Payment system is still initializing. Please wait a moment and try again.");
                }

                if (!this.cart || this.cart.length === 0) {
                    throw new Error("Your cart is empty. Please add items before placing an order.");
                }

                if (!this.hasBilling) {
                    throw new Error("Please complete your shipping address before placing an order.");
                }

                // Save checkout state before redirecting to Stripe
                this.saveCheckoutState();

                console.log("🔵 [FIREBASE STRIPE] Creating checkout session...");
                
                // Prepare line items for Stripe
                const productLineItems = this.cart.map(item => {
                    const productData = {
                        name: item.brand || item.name || 'Product',
                        // Note: Not sending images to Stripe - they're stored in uploadedImageUrls in the order
                    };
                    
                    // Calculate base price + extra fees (e.g. Portrait Style)
                    let unitAmount = Math.round(item.price * 100);
                    const perUnitPortraits = this.getPortraitStyleCountForCartItem(item);
                    
                    let descriptionParts = [];
                    if (item.size) {
                        descriptionParts.push(`Size: ${item.size}`);
                    }
                    
                    if (perUnitPortraits > 0) {
                        const extraFee = perUnitPortraits * 5; // $5 per portrait style
                        unitAmount += Math.round(extraFee * 100);
                        descriptionParts.push(`Portrait Style (+$${extraFee})`);
                    }
                    
                    if (descriptionParts.length > 0) {
                        productData.description = descriptionParts.join(', ');
                    }
                    
                    return {
                        price_data: {
                            currency: 'usd',
                            product_data: productData,
                            unit_amount: unitAmount,
                        },
                        quantity: item.quantity || 1,
                    };
                });

                let lineItems = productLineItems;

                const subtotalCents = Math.round(this.adjustedSubtotal * 100);
                const discountCents = Math.round((this.couponDiscount + this.firstPurchaseDiscount) * 100);
                const maxDiscountCents = Math.min(discountCents, subtotalCents);

                if (maxDiscountCents > 0 && subtotalCents > 0 && productLineItems.length > 0) {
                    const originalProductsTotalCents = productLineItems.reduce((sum, li) => {
                        const qty = li.quantity || 1;
                        return sum + li.price_data.unit_amount * qty;
                    }, 0);

                    let remainingDiscountCents = Math.min(maxDiscountCents, originalProductsTotalCents);
                    let remainingProductsCents = originalProductsTotalCents;

                    productLineItems.forEach((li, index) => {
                        const qty = li.quantity || 1;
                        const itemOriginalTotal = li.price_data.unit_amount * qty;

                        if (index === productLineItems.length - 1) {
                            const newItemTotal = Math.max(0, remainingProductsCents - remainingDiscountCents);
                            li.price_data.unit_amount = Math.floor(newItemTotal / qty);
                        } else {
                            const proportion = itemOriginalTotal / originalProductsTotalCents;
                            const itemDiscount = Math.round(remainingDiscountCents * proportion);
                            const newItemTotal = Math.max(0, itemOriginalTotal - itemDiscount);
                            li.price_data.unit_amount = Math.floor(newItemTotal / qty);

                            remainingDiscountCents -= itemDiscount;
                            remainingProductsCents -= itemOriginalTotal;
                        }
                    });
                }

                // Add shipping as a line item (not discounted)
                if (this.shippingFee > 0) {
                    lineItems.push({
                        price_data: {
                            currency: 'usd',
                            product_data: {
                                name: this.shippingMethodLocal === 'express' ? 'Express Shipping' : 'Standard Shipping',
                            },
                            unit_amount: Math.round(this.shippingFee * 100),
                        },
                        quantity: 1,
                    });
                }

                console.log('🔵 [CHECKOUT] Preparing order metadata...');
                console.log('🔵 [CHECKOUT] Raw cart items:', this.cart.length);
                
                // Prepare order metadata
                const now = new Date();
                const allImageUrls = [];
                
                console.log('🔵 [CHECKOUT] Sanitizing cart data...');
                
                // Sanitize cart data - remove File objects and non-serializable data
                const sanitizedCart = this.cart.map((item, index) => {
                    console.log(`🔵 [CHECKOUT] Sanitizing item ${index + 1}:`, item.brand || item.name);
                    console.log(`🔵 [CHECKOUT] Item has imageUrls:`, !!item.imageUrls, 'count:', item.imageUrls?.length || 0);
                    console.log(`🔵 [CHECKOUT] Item has uploadedImages:`, !!item.uploadedImages, 'count:', item.uploadedImages?.length || 0);
                    
                    // Collect image URLs
                    if (item.imageUrls && Array.isArray(item.imageUrls)) {
                        console.log(`✅ [CHECKOUT] Collecting ${item.imageUrls.length} image URLs from item ${index + 1}`);
                        allImageUrls.push(...item.imageUrls);
                    }
                    
                    // Create a clean copy without File objects
                    const cleanItem = {};
                    let removedKeys = [];
                    
                    // Save portrait count explicitly for order history
                    cleanItem.portraitCount = this.getPortraitStyleCountForCartItem(item);
                    
                    for (const key in item) {
                        const value = item[key];
                        // Skip File objects, functions, and undefined values
                        if (value instanceof File || 
                            value instanceof Blob || 
                            typeof value === 'function' || 
                            value === undefined) {
                            removedKeys.push(key);
                            continue;
                        }
                        // Keep everything else
                        cleanItem[key] = value;
                    }

                    // Explicitly save portrait style count for order history
                    const portraitCount = this.getPortraitStyleCountForCartItem(item);
                    if (portraitCount > 0) {
                        cleanItem.portraitStyleCount = portraitCount;
                    }
                    
                    if (removedKeys.length > 0) {
                        console.log(`⚠️ [CHECKOUT] Removed keys from item ${index + 1}:`, removedKeys);
                    }
                    
                    return cleanItem;
                });
                
                console.log('✅ [CHECKOUT] Cart sanitized');
                console.log('🔵 [CHECKOUT] Total image URLs collected:', allImageUrls.length);
                console.log('🔵 [CHECKOUT] Image URLs:', allImageUrls);

                console.log('🔵 [CHECKOUT] Creating pending order data...');
                
                // Store full order data in Firestore first (to avoid Stripe metadata 500 char limit)
                const pendingOrderData = {
                    order: sanitizedCart,
                    name: this.firstName + " " + this.lastName,
                    total: this.orderTotal.toFixed(2),
                    subtotal: this.adjustedSubtotal.toFixed(2),
                    shippingFee: this.shippingFee.toFixed(2),
                    shippingMethod: this.shippingMethodLocal,
                    couponDiscount: this.couponDiscount.toFixed(2),
                    couponCode: this.couponCode || '',
                    firstPurchaseDiscount: this.firstPurchaseDiscount.toFixed(2),
                    portraitStyleFee: this.portraitStyleFee.toFixed(2),
                    address: {
                        streetAddress: this.userStreetAddress,
                        city: this.userCity,
                        state: this.userState,
                        country: this.userCountry,
                        zip: this.mailingZip,
                    },
                    emailAddress: this.userEmail,
                    accountEmail: this.loggedInEmail,
                    accountUid: auth.currentUser.uid,
                    orderPaid: false,
                    timeOrdered: now.toString(),
                    timeOrderedTs: Date.now(),
                    uploadedImageUrls: allImageUrls,
                    status: 'pending_payment',
                };
                
                console.log('🔵 [CHECKOUT] Pending order data prepared');
                console.log('🔵 [CHECKOUT] Order items:', pendingOrderData.order.length);
                console.log('🔵 [CHECKOUT] Uploaded image URLs:', pendingOrderData.uploadedImageUrls.length);

                console.log('🔵 [CHECKOUT] Saving pending order to Firestore...');
                // Save pending order to Firestore
                const pendingOrdersCollection = collection(db, 'pending_orders');
                const pendingOrderRef = await addDoc(pendingOrdersCollection, pendingOrderData);
                
                console.log("✅ [CHECKOUT] Pending order created:", pendingOrderRef.id);
                console.log("🔵 [FIREBASE STRIPE] Pending order created:", pendingOrderRef.id);

                // Minimal metadata (under 500 chars per field)
                const orderMetadata = {
                    pendingOrderId: pendingOrderRef.id,
                    name: this.firstName + " " + this.lastName,
                    total: this.orderTotal.toFixed(2),
                    email: this.userEmail,
                    uid: auth.currentUser.uid,
                    itemCount: this.cart.length.toString(),
                };

                // Prepare session data for Stripe
                const sessionData = {
                    line_items: lineItems,
                    success_url: window.location.origin + '/order-success?session_id={CHECKOUT_SESSION_ID}',
                    cancel_url: window.location.origin + '/cart?payment=cancelled',
                    metadata: orderMetadata,
                    mode: 'payment',
                    billing_address_collection: 'required',
                };

                // Create checkout session using Firebase Stripe extension
                console.log("🔵 [FIREBASE STRIPE] Calling createCheckoutSession...");
                console.log("🔵 [FIREBASE STRIPE] Line items:", JSON.stringify(lineItems, null, 2));
                
                let session;
                try {
                    // Set a longer timeout for createCheckoutSession
                    const sessionPromise = createCheckoutSession(this.stripePayments, sessionData);
                    const timeoutPromise = new Promise((_, reject) => 
                        setTimeout(() => reject(new Error('Checkout session creation timeout after 60s')), 60000)
                    );
                    
                    session = await Promise.race([sessionPromise, timeoutPromise]);
                    
                    console.log("✅ [FIREBASE STRIPE] Checkout session document created:", session);
                    console.log("🔵 [FIREBASE STRIPE] Session ID:", session.id);
                    console.log("🔵 [FIREBASE STRIPE] Session path:", session.path);
                } catch (error) {
                    console.error("❌ [FIREBASE STRIPE] Failed to create checkout session:", error);
                    console.error("❌ [FIREBASE STRIPE] Error message:", error.message);
                    console.error("❌ [FIREBASE STRIPE] Error stack:", error.stack);
                    throw error;
                }

                // Check if the session object already has a URL (extension returns it immediately)
                if (session.url) {
                    console.log("✅ [FIREBASE STRIPE] URL found in session object!");
                    console.log("🔵 [FIREBASE STRIPE] Redirect URL:", session.url);
                    console.log("🚀 [FIREBASE STRIPE] Redirecting to Stripe Checkout NOW...");
                    
                    // Redirect immediately
                    window.location.href = session.url;
                    return; // Exit the function
                }

                // Fallback: if no URL in session object, wait for snapshot update
                console.log("⏳ [FIREBASE STRIPE] No URL in session object, waiting for snapshot...");
                
                // Set a timeout to prevent infinite waiting
                let timeoutId = setTimeout(() => {
                    console.error("❌ [FIREBASE STRIPE] Timeout waiting for Stripe session");
                    this.paymentError = "Payment session timeout. Please try again.";
                    this.noOrder = true;
                    if (this.$swal) {
                        this.$swal({
                            icon: 'error',
                            title: 'Payment Timeout',
                            text: 'Payment session timeout. Please try again.',
                            confirmButtonText: 'OK',
                            confirmButtonColor: '#d33',
                        });
                    }
                }, 30000); // 30 second timeout

                // Listen for session completion (fallback)
                const unsubscribe = onSnapshot(session, async (snap) => {
                    console.log("🔵 [FIREBASE STRIPE] Snapshot update received");
                    
                    const data = snap.data();
                    const { error, sessionId, url } = data || {};
                    
                    console.log("🔵 [FIREBASE STRIPE] Snapshot data - error:", error, "sessionId:", sessionId, "url:", url);
                    
                    if (error) {
                        clearTimeout(timeoutId);
                        console.error("❌ [FIREBASE STRIPE] Session error:", error);
                        this.paymentError = error.message || "Payment session error";
                        this.noOrder = true;
                        if (this.$swal) {
                            this.$swal({
                                icon: 'error',
                                title: 'Payment Error',
                                text: this.paymentError,
                                confirmButtonText: 'OK',
                                confirmButtonColor: '#d33',
                            });
                        }
                        unsubscribe();
                        return;
                    }

                    if (url) {
                        clearTimeout(timeoutId);
                        console.log("✅ [FIREBASE STRIPE] Got URL from snapshot, redirecting...");
                        window.location.href = url;
                        unsubscribe();
                        return;
                    }

                    if (sessionId) {
                        clearTimeout(timeoutId);
                        console.log("✅ [FIREBASE STRIPE] Got sessionId from snapshot, redirecting...");
                        const { error: stripeError } = await this.stripe.redirectToCheckout({ sessionId });
                        
                        if (stripeError) {
                            console.error("❌ [FIREBASE STRIPE] Redirect error:", stripeError);
                            this.paymentError = stripeError.message;
                            this.noOrder = true;
                            if (this.$swal) {
                                this.$swal({
                                    icon: 'error',
                                    title: 'Redirect Error',
                                    text: this.paymentError,
                                    confirmButtonText: 'OK',
                                    confirmButtonColor: '#d33',
                                });
                            }
                        }
                        unsubscribe();
                        return;
                    }
                });

            } catch (error) {
                console.error("❌ [FIREBASE STRIPE] Error during payment process:", error);
                console.error("❌ [FIREBASE STRIPE] Error details:", error.message);
                
                // Determine error message
                let errorMessage = 'Payment failed. Please try again.';
                const rawMessage = error && error.message ? String(error.message) : '';

                if (rawMessage) {
                    // Special-case Stripe/firestore-stripe-payments timeouts
                    if (
                        rawMessage.includes('Timeout while waiting for session response') ||
                        rawMessage.toLowerCase().includes('checkout session creation timeout')
                    ) {
                        errorMessage = 'We could not start your secure payment session. This is often caused by a browser extension (like an ad blocker) blocking connections to Stripe/Firebase or a temporary connection issue. Please disable such extensions for this site or try again in a moment.';
                    } else {
                        errorMessage = rawMessage;
                    }
                }
                
                // Show error alert
                if (this.$swal) {
                    this.$swal({
                        icon: 'error',
                        title: 'Payment Failed',
                        text: errorMessage,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#d33',
                    });
                } else {
                    alert('❌ Payment Failed\n\n' + errorMessage + '\n\nPlease check your details and try again.');
                }
                
                this.paymentError = errorMessage;
                this.noOrder = true;
            }
        },
        closeModal() {
            this.showModal = false;
        },
        scrollToTop() {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                try {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch (e) {
                    // fallback for older browsers
                    window.scrollTo(0, 0);
                }
            }
        },
        computeLineHeights() {
            // Align step numbers vertically with the center of each section header
            const sections = [this.$refs.section1, this.$refs.section2, this.$refs.section3, this.$refs.section4];
            const headers = sections.map((section) => (section ? section.querySelector('.section-header') || section : null));

            if (!headers[0] || headers.some((el) => !el)) {
                return;
            }

            const stepperEl = this.$refs.stepper;
            if (!stepperEl || !stepperEl.getBoundingClientRect) return;

            const stepHeight = 56; // diameter of step circle
            const nextHeights = [0, 0, 0];

            // Vertical center of each header (relative to viewport)
            const centers = headers.map((el) => {
                const rect = el.getBoundingClientRect();
                return rect.top + rect.height / 2;
            });

            // Adjust top padding so step 1 circle is centered with the first header
            const stepperRect = stepperEl.getBoundingClientRect();
            const desiredCenterOffset = centers[0] - stepperRect.top - stepHeight / 2;
            this.stepperOffset = Math.max(0, Math.round(desiredCenterOffset));

            // For each connector, make the line fill the distance between the
            // bottom edge of the upper circle and the top edge of the lower circle.
            for (let i = 0; i < 3; i += 1) {
                const centerDelta = centers[i + 1] - centers[i];
                let raw = centerDelta - stepHeight; // space between the two circle edges
                if (!Number.isFinite(raw)) raw = 40;
                nextHeights[i] = Math.max(32, Math.round(raw));
            }

            this.lineHeights = nextHeights;
        },
        updateLinesSmoothly() {
            const start = performance.now();
            const duration = 600;
            
            const animate = (time) => {
                this.computeLineHeights();
                if (time - start < duration) {
                    requestAnimationFrame(animate);
                }
            };
            requestAnimationFrame(animate);
        },
        recalculateLinesDebounced(delay = 560) {
            if (this._lineHeightTimer) window.clearTimeout(this._lineHeightTimer);
            this._lineHeightTimer = window.setTimeout(() => {
                this.$nextTick(() => {
                    window.requestAnimationFrame(() => this.computeLineHeights());
                });
            }, delay);
        },
    },
    mounted() {
        // Restore checkout state if user is returning from cancelled/failed payment
        const restored = this.restoreCheckoutState();
        if (restored) {
            console.log('📋 Checkout form restored - user returned from payment');
        }
        
        // Handle authentication state
        onAuthStateChanged(auth, (user) => {
            if (user) {
                console.log("logged in");
                this.loggedInEmail = user.email;
            } else {
                console.log("not logged in");
            }
        });

        // Initialize Firebase Stripe Payments
        (async () => {
            try {
                console.log("🔵 [FIREBASE STRIPE] ========================================");
                console.log("🔵 [FIREBASE STRIPE] Initializing Firebase Stripe Payment System...");
                console.log("🔵 [FIREBASE STRIPE] ========================================");
                
                // Load the Stripe instance
                const envKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
                const fallbackKey = "pk_live_510zJwUBkDGtaNC4PTrY7FJuSE78w1PWAkuIb00RdbWTCSHIjVpK0M1bXpWoKuTTHJLV00AKXW95cClYep2PpxKjf00VQSp2PEC";
                const stripeKey = envKey || fallbackKey;
                
                console.log("🔵 [FIREBASE STRIPE] Stripe Publishable Key source:", envKey ? "Environment Variable" : "Fallback (hardcoded)");
                console.log("🔵 [FIREBASE STRIPE] Key starts with:", stripeKey.substring(0, 20) + "...");
                
                console.log("🔵 [FIREBASE STRIPE] Loading Stripe SDK...");
                this.stripe = await loadStripe(stripeKey);

                if (!this.stripe) {
                    console.error("❌ [FIREBASE STRIPE] Failed to load Stripe SDK - loadStripe returned null");
                    throw new Error("Failed to load Stripe SDK");
                }

                console.log("✅ [FIREBASE STRIPE] Stripe SDK loaded successfully");

                // Initialize Firebase Stripe Payments
                console.log("🔵 [FIREBASE STRIPE] Initializing Firebase Stripe Payments extension...");
                this.stripePayments = getStripePayments(app, {
                    productsCollection: 'products',
                    customersCollection: 'customers',
                });
                
                console.log("✅ [FIREBASE STRIPE] Firebase Stripe Payments initialized");
                console.log("✅ [FIREBASE STRIPE] Payment system is ready to use");
                console.log("🔵 [FIREBASE STRIPE] ========================================");
            } catch (error) {
                console.error("❌ [FIREBASE STRIPE] ========================================");
                console.error("❌ [FIREBASE STRIPE] INITIALIZATION FAILED");
                console.error("❌ [FIREBASE STRIPE] ========================================");
                console.error("❌ [FIREBASE STRIPE] Error:", error.message);
                console.error("❌ [FIREBASE STRIPE] Stack:", error.stack);
                console.error("❌ [FIREBASE STRIPE] ========================================");
                
                // Set user-friendly error message
                let userMessage = "Unable to load payment system. ";
                
                if (error.message?.includes('API key') || error.message?.includes('STRIPE')) {
                    userMessage += "There's a configuration issue with the payment system. Please contact support.";
                } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
                    userMessage += "Please check your internet connection and try again.";
                } else {
                    userMessage += "Please try again later or contact support.";
                }
                
                this.paymentError = userMessage;
                if (this.$swal) {
                    this.$swal({
                        icon: 'error',
                        title: 'Payment System Error',
                        text: userMessage,
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#d33',
                    });
                }
            }
        })();

        // Initialize and bind resize listeners for dynamic stepper line heights
        this.$nextTick(() => this.recalculateLinesDebounced(50));
        this._resizeHandler = () => this.recalculateLinesDebounced(50);
        window.addEventListener('resize', this._resizeHandler, { passive: true });
        window.addEventListener('orientationchange', this._resizeHandler, { passive: true });

        // Check first purchase
        this.$store.dispatch('checkFirstTimeBuyer').then(res => {
            this.isFirstPurchase = res;
        });
    }

    ,beforeDestroy() {
        if (this._lineHeightTimer) window.clearTimeout(this._lineHeightTimer);
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
            window.removeEventListener('orientationchange', this._resizeHandler);
        }
    }

    };
</script>

<style scoped>
/* Smooth scroll behavior */
html {
    scroll-behavior: smooth;
}

#payment-message {
  color: rgb(105, 115, 134);
  font-size: 16px;
  line-height: 20px;
  padding-top: 12px;
  text-align: center;
}

#payment-element {
  margin-bottom: 24px;
}

#card-element {
  width: 100%;
  height: 40px;
  margin-top: 10px;
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 4px;
}

.coupon {
    display: flex;
    gap: 15px;
}

.billing-info {
    display: grid;
    grid-template-columns: 90px 1fr 360px;
    grid-template-areas: "steps main summary";
    align-items: stretch;
    gap: 20px;
    margin-top: 0;
    padding-top: 15px;
    width: 100%;
    min-width: 0;
}

.billing-info h4 {
    margin-bottom: 15px;
    position: relative;
}

.form {
    grid-area: main;
    flex: 3;
    display: flex;
    flex-direction: column;
    gap: 15px;
    min-width: 0;
}

form input,
form textarea {
    border-radius: 0px;
    padding: 0px;
    box-sizing: border-box;
}

.form-group {
    display: flex;
    justify-content: space-between;
    gap: 20px;
}
.form-control {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
}

label {
    font-size: 1.5rem;
}

.form-control input,
.form-control textarea {
    padding: 12px 16px;
    outline: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: #f9f9f9;
    width: 100%;
    border: 2px solid #e6d1c7;
    border-radius: 12px;
    font-size: 15px;
}

textarea {
    resize: vertical;
}

.form-control input:focus,
.form-control textarea:focus {
    border: 2px solid #e37267;
    background-color: white;
    box-shadow: 0 0 0 4px rgba(227, 114, 103, 0.1);
    transform: translateY(-1px);
}

aside { grid-area: summary; flex: 1; padding: 0; min-width: 0; }
aside .card { 
    padding: 28px; 
    border: 2px solid #e37267; 
    border-radius: 20px; 
    background: linear-gradient(135deg, #fffef9 0%, #fff5f0 100%);
    margin-bottom: 20px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(227, 114, 103, 0.12);
}

aside .card:hover {
    box-shadow: 0 8px 24px rgba(227, 114, 103, 0.2);
    transform: translateY(-2px);
}

aside h4 { 
    padding-bottom: 16px; 
    color: #e37267; 
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
}

.coupon-input { 
    display: flex;
    gap: 12px; 
    margin-bottom: 20px;
}

.coupon-input input { 
    flex: 1;
    background: white; 
    padding: 14px 16px;
    border: 2px solid #e6d1c7;
    border-radius: 12px;
    font-size: 15px;
    transition: all 0.3s ease;
    outline: none;
}

.coupon-input input:focus {
    border-color: #e37267;
    box-shadow: 0 0 0 4px rgba(227, 114, 103, 0.1);
}

.coupon-input .apply { 
    background: linear-gradient(135deg, #e37267, #f4a89f);
    color: white; 
    border: none; 
    border-radius: 12px;
    padding: 14px 24px;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(227, 114, 103, 0.3);
}

.coupon-input .apply:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(227, 114, 103, 0.4);
}

.coupon-input .apply:active {
    transform: translateY(0);
}
.summary-row { display: flex; justify-content: space-between; padding: 10px 0; font-size: 15px; }
.summary-row .label { color: #666; font-weight: 500; }
.summary-row .value { color: #1b1b1b; font-weight: 600; }
.summary-row .discount { color: #10b981; }
.summary-row.total { padding: 16px 0 8px; }
.summary-row.total .label, .summary-row.total .value { font-weight: 700; font-size: 2.2rem; color: #1b1b1b; }
.divider { height: 1px; background: #e6d1c7; opacity: 1; margin: 12px 0; }

.order-summary-inline {
    margin-top: 32px;
    padding: 24px;
    background: white;
    border-radius: 16px;
    border: 2px solid #e6d1c7;
}

.order-summary-inline h5 {
    margin: 0 0 16px 0;
    font-size: 1.8rem;
    font-weight: 700;
    color: #1b1b1b;
}

.submit-pay {
    background: linear-gradient(135deg, #635BFF, #5851EA) !important;
    font-size: 17px !important;
    padding: 16px 32px !important;
    margin-top: 24px;
}

.submit-pay:hover {
    background: linear-gradient(135deg, #5851EA, #4845D2) !important;
}

.submit-pay:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.submit-continue {
    width: 100%;
    margin-top: 24px;
}

.trash {
    border: none;
    background: transparent;
    cursor: pointer;
    color: #e37267;
    padding: 8px;
    border-radius: 8px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.trash:hover {
    background: #fee;
    transform: scale(1.1);
}

.trash svg {
    width: 16px;
    height: 16px;
}
/* Section Header Improvements */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 4px 0;
  gap: 20px;
}

.section-header-left {
  flex: 1;
  min-width: 0;
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.section-title-row h4 {
  margin: 0;
  font-size: 1.9rem;
  font-weight: 700;
  color: #1b1b1b;
  letter-spacing: -0.02em;
}

.section-preview {
  margin-top: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
  border-radius: 12px;
  border-left: 4px solid #e37267;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.preview-name {
  font-weight: 700;
  font-size: 16px;
  color: #1b1b1b;
  margin-bottom: 6px;
}

.preview-address,
.preview-method,
.preview-payment {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.preview-method {
  font-weight: 600;
  color: #e37267;
}

.preview-payment {
  color: #10b981;
  font-weight: 600;
}

.edit-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #e37267, #f4a89f);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(227, 114, 103, 0.2);
  flex-shrink: 0;
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(227, 114, 103, 0.35);
}

.edit-btn:active {
  transform: translateY(0);
}

.edit-btn svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.address-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.address-label .label-hint {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}
.stepper { 
    grid-area: steps; 
    display: flex; 
    flex-direction: column; 
    align-items: center; 
    gap: 0;
    padding-top: 28px;
}

.stepper .step { 
    width: 56px; 
    height: 56px; 
    border-radius: 50%; 
    border: 3px solid #e6d1c7; 
    display: flex; 
    align-items: center; 
    justify-content: center; 
    background: white;
    font-weight: 700;
    font-size: 18px;
    color: #999;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;
}

.stepper .line { 
    width: 2px; 
    height: 70px; /* default, will be overridden by inline style using lineHeights */
    background: #e6d1c7;
    border-radius: 2px;
    position: relative;
    overflow: hidden;
}

.stepper .line::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 0;
    background: linear-gradient(180deg, #e37267, #f4a89f);
    transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.stepper .line.active::after { 
    height: 100%;
}

.stepper .step::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e37267;
    transform: scale(0);
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.stepper .step.current { 
    border-color: #e37267;
    background: linear-gradient(135deg, #fff5f0, #ffe1cf);
    color: #e37267;
    box-shadow: 0 4px 12px rgba(227, 114, 103, 0.3);
    transform: scale(1.1);
}

.stepper .step.current::after {
    transform: scale(1.2);
    opacity: 0.3;
    animation: pulse 2s ease-in-out infinite;
}

.stepper .step.done {
    border-color: #10b981;
    background: linear-gradient(135deg, #d1fae5, #a7f3d0);
    color: #10b981;
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1.2);
        opacity: 0.3;
    }
    50% {
        transform: scale(1.4);
        opacity: 0;
    }
}
.order-total-row { display: flex; justify-content: flex-end; gap: 8px; margin: 10px 0 15px; }

.shipping-info-box {
    padding: 24px;
    background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
    border: 2px solid #e6d1c7;
    border-radius: 12px;
    margin-bottom: 20px;
}

.shipping-notice {
    margin: 0;
    font-size: 15px;
    color: #666;
    text-align: center;
    line-height: 1.6;
}

.shipping-methods { display: flex; flex-direction: column; gap: 15px; }

.powered-by-yunexpress {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 10px;
    opacity: 0.6;
    font-size: 12px;
    color: #838383;
}

.shipping-option { 
    display: flex; 
    flex-direction: column;
    padding: 20px; 
    border: 2px solid #e6d1c7; 
    border-radius: 12px; 
    cursor: pointer; 
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: white;
    margin-bottom: 16px;
}

.shipping-option:last-child {
    margin-bottom: 0;
}

.shipping-option:hover {
    border-color: #e37267;
    box-shadow: 0 4px 16px rgba(227, 114, 103, 0.12);
    transform: translateY(-2px);
}

.shipping-option.selected {
    border-color: #e37267;
    border-width: 2px;
    background: linear-gradient(135deg, #fef9f6 0%, #fff5f0 100%);
    box-shadow: 0 4px 20px rgba(227, 114, 103, 0.2);
}

.shipping-option-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.shipping-option input[type="radio"] { 
    margin: 0;
    cursor: pointer;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
}

.shipping-title-row {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.shipping-name { 
    font-weight: 700; 
    font-size: 17px;
    color: #1b1b1b;
}

.shipping-price { 
    font-size: 20px; 
    font-weight: 700; 
    color: #e37267;
}

.shipping-badge {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.shipping-description {
    padding-left: 32px;
}

.shipping-info-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}

.info-icon {
    color: #999;
    flex-shrink: 0;
}

.shipping-time { 
    font-size: 14px; 
    color: #666;
    font-weight: 600;
}

.shipping-desc-text {
    font-size: 13px;
    line-height: 1.6;
    color: #666;
    margin: 0;
}
.shipping-loading { display: flex; align-items: center; gap: 12px; padding: 20px; background: #f9f9f9; border-radius: 8px; }
.spinner-small { width: 20px; height: 20px; border: 3px solid #e6d1c7; border-top-color: #e37267; border-radius: 50%; animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.free-shipping-notice { margin-top: 15px; padding: 12px; background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 4px; color: #1e40af; font-size: 14px; }
.shipping-provider { margin-top: 10px; text-align: center; color: #999; }
.review-products { margin: 10px 0; }
.cart-header-row { display: grid; grid-template-columns: 1fr 180px 140px; color: #e4b9a0; font-weight: 600; margin-bottom: 0.75rem; }
.divider { height: 1px; background: #e6d1c7; margin: 0.75rem 0 0.5rem; }
.cart-row { display: grid; grid-template-columns: 1fr 180px 140px 32px; align-items: center; gap: 1rem; padding: 1rem 0; }
.product-cell { display: grid; grid-template-columns: 90px 1fr; align-items: center; gap: 1rem; }
.thumb { width: 90px; height: 88px; border-radius: 10px; object-fit: cover; }
.product-meta { display: flex; flex-direction: column; gap: 0.4rem; }
.product-title { font-weight: 600; font-size: 1.8rem; }
.chip { display: inline-flex; align-items: center; height: 20px; padding: 0 10px; border-radius: 10px; font-size: 1.2rem; }
.in-stock { background: #ffe9dd; color: #e37267; white-space: nowrap; align-self: flex-start; }
.qty-cell { display: flex; justify-content: flex-end; }
.qty-control { display: inline-flex; align-items: center; border: 2px solid #d78d86; border-radius: 20px; padding: 4px 10px; min-width: 100px; justify-content: space-between; }
.qty-btn { background: transparent; color: #d78d86; border: none; font-size: 2rem; line-height: 1; cursor: pointer; }
.qty-value { color: #d78d86; font-weight: 900; }
.price-cell { text-align: right; font-weight: 600; font-size: 1.8rem; }
.old-price { color: #d78d86; text-decoration: line-through; margin-left: 10px; font-weight: 400; opacity: 0.8; }
.trash { border: none; background: #fffef9; cursor: pointer; font-size: 1.6rem; }
.col-price { text-align: right; }
.col-qty { text-align: center; }
.col-product { text-align: left; }
.payment-error { color: #e37267; margin-top: 8px; }
@media (max-width: 790px) {
    .billing-info { grid-template-columns: 44px 1fr; grid-template-areas: "steps main" "summary summary"; }
    .form-group { flex-direction: column; width: 100%; }
    aside, .form { width: 100%; max-width: 100%; }
    aside .card { width: 100%; }
    /* Responsive header/row columns: product | qty | price | delete */
    .cart-header-row { grid-template-columns: 1fr minmax(84px, 26vw) minmax(64px, 22vw); font-size: 4.2vw; }
    .cart-row { grid-template-columns: 1fr minmax(84px, 26vw) minmax(64px, 22vw) 24px; }
    .product-cell { grid-template-columns: 60px 1fr; }
    .product-meta { min-width: 0; }
    .product-title { font-size: 3.2vw; word-break: break-word; overflow-wrap: anywhere; }
    .thumb { width: 60px; height: 58px; border-radius: 10px; object-fit: cover; }
    /* Quantity control scales with viewport */
    .qty-control { min-width: min(90px, 24vw); padding: 2px 8px; }
    .qty-btn { font-size: 4.2vw; }
    .qty-value { font-size: 3.2vw; }
    /* Price and delete icons scale to fit */
    .price-cell { font-size: 3.6vw; }
    .trash { font-size: 3.6vw; }
    .old-price { font-size: 3vw; margin-left: 6px; }
    .chip { height: 18px; font-size: 3vw; padding: 0 6px; }

    /* Shrink left stepper and connectors */
    .stepper { gap: 8px; }
    .stepper .step { width: 30px; height: 30px; }
    .stepper .step span { font-size: 3.2vw; line-height: 1; }
    .stepper .line { height: 36px; }

    /* Prevent heading/summary/Edit overlap */
    .form-section h4 { font-size: 4.2vw; padding-right: 60px; }
    .section-summary { display: block; margin-top: 4px; font-size: 3.4vw; }
    .edit { right: 8px; top: 2px; padding: 2px 8px; font-size: 3.4vw; }
}

@media (max-width: 790px) {
    /* Stack product text under image on very narrow screens */
    .cart-row { grid-template-columns: 1fr minmax(80px, 26vw) minmax(62px, 22vw) 24px; }
    .product-cell { grid-template-columns: 1fr; grid-template-rows: auto auto; align-items: start; }
    .thumb { width: 60px; height: 58px; }
    .product-meta { min-width: 0; margin-top: 6px; text-align: left; }
    .product-title { font-size: 3.8vw; word-break: normal; overflow-wrap: normal; }
}

/* iPad and similar widths: stack text under image even in landscape */
@media (max-width: 1100px) {
    /* On tablets, move summary (card-summary) below the form */
    .billing-info { grid-template-columns: 70px 1fr; grid-template-areas: "steps main" "summary summary"; }
    aside, .form { width: 100%; max-width: 100%; }
    aside .card { width: 100%; }
    .product-cell { grid-template-columns: 1fr; grid-template-rows: auto auto; align-items: start; }
    .product-meta { min-width: 0; margin-top: 6px; }
    .product-title { font-size: clamp(14px, 1.8vw, 18px); word-break: normal; overflow-wrap: anywhere; }
}

.form-section {
    border: 2px solid #e6d1c7;
    padding: 24px;
    border-radius: 16px;
    background: white;
    box-shadow: 0 2px 8px rgba(227, 114, 103, 0.08);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    margin-bottom: 20px;
}

.form-section:last-child {
    margin-bottom: 0;
}

.form-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #e37267, #f4a89f);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-section:hover {
    border-color: #e37267;
    box-shadow: 0 4px 16px rgba(227, 114, 103, 0.15);
    transform: translateY(-2px);
}

.form-section.active {
    border-color: #e37267;
}

.form-section.active::before {
    transform: scaleX(1);
}

.form-section h4 {
    cursor: pointer;
    margin-bottom: 10px;
    font-size: 1.8rem;
    color: #1b1b1b;
    display: flex;
    align-items: center;
    gap: 10px;
    transition: color 0.3s ease;
}

.form-section h4:hover {
    color: #e37267;
}

.form-section h4::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #e6d1c7;
    transition: all 0.3s ease;
}

.form-section.active h4::before {
    background: #e37267;
    box-shadow: 0 0 0 4px rgba(227, 114, 103, 0.2);
}

.collapsible-content {
    overflow: hidden;
    max-height: 0;
    opacity: 0;
    transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1), 
                opacity 0.4s ease,
                padding 0.4s ease;
}

.collapsible-content.active {
    padding: 20px 0 10px;
    opacity: 1;
    animation: slideInContent 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideInContent {
    from {
        transform: translateY(-10px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.form-control {
    margin-bottom: 15px;
}

/* Enhanced Payment Section Styling */
.payment-method-header {
    margin-bottom: 25px;
    padding: 20px;
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
    border-radius: 12px;
    border: 2px solid #e6e9ff;
    box-shadow: 0 2px 8px rgba(99, 91, 255, 0.08);
}

.payment-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    flex-wrap: wrap;
    gap: 10px;
}

.payment-method-header h5 {
    margin: 0;
    color: #1a202c;
    font-size: 2rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
}

.trust-badges {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.trust-badge {
    padding: 6px 12px;
    background: #10b981;
    color: white;
    border-radius: 20px;
    font-size: 1.2rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.button-container {
    margin-top: 24px;
    width: 100%;
}

.submit {
    background: linear-gradient(135deg, #e37267, #f4a89f);
    color: white;
    padding: 16px 32px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    width: 100%;
}

.submit:hover {
    background: linear-gradient(135deg, #d66257, #e39889);
}

.submit:active {
    background: linear-gradient(135deg, #c55247, #d28879);
}

.payment-description {
    margin: 0 0 15px 0;
    color: #4a5568;
    font-size: 1.4rem;
    line-height: 1.6;
    padding: 12px;
    background: #ffffff;
    border-radius: 8px;
    border-left: 3px solid #635BFF;
}

.payment-description strong {
    color: #635BFF;
    font-weight: 700;
}

.accepted-cards-row {
    margin-top: 15px;
}

.cards-label {
    font-size: 1.3rem;
    color: #2d3748;
    font-weight: 600;
    margin-bottom: 10px;
}

.card-logos {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
}

.card-logo {
    padding: 8px 14px;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 6px;
    font-size: 1.2rem;
    font-weight: 700;
    color: #2d3748;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-logo:hover {
    border-color: #635BFF;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(99, 91, 255, 0.15);
}

.card-logo.visa {
    color: #1434CB;
    border-color: #1434CB;
}

.card-logo.mastercard {
    color: #EB001B;
    border-color: #EB001B;
}

.card-logo.amex {
    color: #006FCF;
    border-color: #006FCF;
}

.card-logo.discover {
    color: #FF6000;
    border-color: #FF6000;
}

.payment-element-wrapper {
    margin: 20px 0;
    padding: 0;
    background: transparent;
    border-radius: 0;
    border: none;
    box-shadow: none;
}

#payment-element {
    min-height: auto;
}

.payment-error {
    color: #c53030;
    font-size: 1.4rem;
    margin-top: 15px;
    padding: 15px;
    background: #fff5f5;
    border-radius: 8px;
    border-left: 4px solid #e53e3e;
    display: flex;
    align-items: center;
    gap: 10px;
    box-shadow: 0 2px 6px rgba(229, 62, 62, 0.1);
}

.error-icon {
    font-size: 2rem;
    flex-shrink: 0;
}

.security-footer {
    margin-top: 25px;
    padding: 20px;
    background: #f7fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.stripe-powered {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e2e8f0;
}

.powered-text {
    font-size: 1.3rem;
    color: #718096;
    font-weight: 500;
}

.security-features {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 15px;
}

.security-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    background: white;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
}

.security-item:hover {
    border-color: #10b981;
    box-shadow: 0 2px 6px rgba(16, 185, 129, 0.1);
}

.security-icon {
    font-size: 2rem;
    flex-shrink: 0;
}

.security-label {
    font-size: 1.2rem;
    color: #2d3748;
    font-weight: 600;
    line-height: 1.3;
}

/* Mobile Responsive */
@media (max-width: 768px) {
    /* Make checkout container full-width on mobile to avoid inputs looking cut */
    .container {
        width: 100%;
        margin-inline: 0;
        padding: 0 12px;
    }

    .payment-title-row {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .trust-badges {
        width: 100%;
    }
    
    .security-features {
        grid-template-columns: 1fr;
    }
    
    .card-logos {
        justify-content: flex-start;
    }
    
    /* Hide stepper on mobile, show horizontal progress instead */
    .billing-info {
        display: flex;
        /* Show summary (aside) above the form on small devices */
        flex-direction: column-reverse;
        align-items: stretch;
        gap: 16px;
        padding: 0;
        width: 100%;
    }
    
    .stepper {
        display: none !important;
    }
    
    .form {
        padding: 0;
        width: 100%;
        max-width: 100%;
    }
    
    /* Mobile form sections - Improved spacing and readability */
    .form-section {
        padding: 18px;
        margin-bottom: 12px;
        border-radius: 14px;
        box-shadow: 0 2px 12px rgba(227, 114, 103, 0.1);
    }
    
    /* Remove top gradient bar on small screens to avoid double top line */
    .form-section::before {
        content: none;
    }
    
    .section-header {
        padding: 8px 0;
        gap: 12px;
    }
    
    .section-header-left {
        flex: 1;
        min-width: 0;
    }
    
    .section-title-row {
        margin-bottom: 6px;
    }
    
    .section-title-row h4 {
        font-size: 1.7rem;
        font-weight: 700;
        letter-spacing: -0.01em;
    }
    
    .section-preview {
        padding: 12px 14px;
        font-size: 1.3rem;
        margin-top: 8px;
        border-radius: 10px;
        /* Remove strong left border on mobile to avoid double line with card edge */
        border-left: none;
        box-shadow: 0 0 0 1px rgba(230, 209, 199, 0.9);
    }
    
    .preview-name {
        font-size: 1.45rem;
        margin-bottom: 4px;
    }
    
    .preview-address,
    .preview-method {
        font-size: 1.25rem;
        line-height: 1.5;
    }
    
    .edit-btn {
        padding: 8px 16px;
        font-size: 1.25rem;
        border-radius: 10px;
        flex-shrink: 0;
        white-space: nowrap;
    }
    
    .edit-btn span {
        display: inline;
    }
    
    .edit-btn svg {
        width: 12px;
        height: 12px;
    }
    
    /* Mobile form controls - Better touch targets */
    .form-group {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }
    
    .form-control {
        margin-bottom: 14px;
    }
    
    .form-control label {
        font-size: 1.4rem;
        margin-bottom: 8px;
        font-weight: 600;
        color: #1b1b1b;
    }
    
    .form-control input,
    .form-control textarea,
    .form-control select {
        font-size: 1.6rem;
        padding: 15px 14px;
        border-radius: 10px;
        border: 2px solid #e6d1c7;
        background: #fafafa;
    }
    
    .form-control input:focus,
    .form-control textarea:focus {
        background: white;
        border-color: #e37267;
    }
    
    /* Mobile shipping options - Improved layout */
    .shipping-option {
        padding: 14px;
        margin-bottom: 12px;
        border-radius: 10px;
    }
    
    .shipping-option-header {
        flex-direction: row;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 10px;
    }
    
    .shipping-option input[type="radio"] {
        margin-top: 2px;
    }
    
    .shipping-title-row {
        width: 100%;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }
    
    .shipping-name {
        font-size: 1.5rem;
        font-weight: 700;
    }
    
    .shipping-price {
        font-size: 1.7rem;
        font-weight: 700;
    }
    
    .shipping-badge {
        font-size: 1.3rem;
        padding: 5px 12px;
    }
    
    .shipping-description {
        padding-left: 30px;
    }
    
    .shipping-info-row {
        margin-bottom: 6px;
    }
    
    .shipping-time {
        font-size: 1.3rem;
    }
    
    .shipping-desc-text {
        font-size: 1.25rem;
        line-height: 1.6;
    }
    
    .free-shipping-notice {
        font-size: 1.3rem;
        padding: 10px 12px;
        margin-top: 12px;
    }
    
    /* Mobile review products - Card-based layout */
    .review-products {
        margin: 0;
    }
    
    .review-products h5 {
        font-size: 1.6rem;
        margin-bottom: 12px;
    }
    
    .cart-header-row {
        display: none;
    }
    
    .cart-row {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding: 14px;
        background: #fafafa;
        border-radius: 10px;
        margin-bottom: 12px;
        position: relative;
        border: 1px solid #e6d1c7;
    }
    
    .product-cell {
        display: flex;
        flex-direction: row;
        width: 100%;
        gap: 12px;
        align-items: flex-start;
    }
    
    .thumb {
        width: 70px;
        height: 70px;
        border-radius: 8px;
        flex-shrink: 0;
    }
    
    .product-meta {
        flex: 1;
        min-width: 0;
    }
    
    .product-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 6px;
        line-height: 1.3;
    }
    
    .chip {
        font-size: 1.1rem;
        padding: 4px 8px;
        height: auto;
    }
    
    .qty-cell {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 8px;
        border-top: 1px solid #e6d1c7;
    }
    
    .qty-control {
        border-radius: 10px;
        padding: 6px 12px;
        min-width: 110px;
    }
    
    .qty-btn {
        font-size: 1.8rem;
        padding: 4px 8px;
    }
    
    .qty-value {
        font-size: 1.5rem;
    }
    
    .price-cell {
        width: auto;
        font-size: 1.8rem;
        font-weight: 700;
        text-align: right;
        color: #e37267;
    }
    
    .trash {
        position: absolute;
        top: 12px;
        right: 12px;
        padding: 6px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
    }
    
    .trash:hover {
        background: #fee;
    }
    
    /* Mobile order summary - Sticky on scroll */
    aside {
        padding: 0;
        position: static;
        top: auto;
        width: 100%;
        max-width: 100%;
        margin-top: 8px;
    }
    
    aside .card {
        padding: 18px;
        margin-bottom: 16px;
        border-radius: 14px;
        box-shadow: 0 4px 16px rgba(227, 114, 103, 0.15);
    }
    
    aside h4 {
        font-size: 1.8rem;
        padding-bottom: 12px;
        margin-bottom: 12px;
    }
    
    .summary-title {
        font-size: 1.8rem !important;
        margin-top: 8px;
    }
    
    .coupon-input {
        flex-direction: row;
        gap: 8px;
        margin-bottom: 16px;
    }
    
    .coupon-input input {
        flex: 1;
        font-size: 1.5rem;
        padding: 12px 14px;
        border-radius: 10px;
    }
    
    .coupon-input .apply {
        padding: 12px 20px;
        font-size: 1.4rem;
        border-radius: 10px;
        white-space: nowrap;
    }
    
    .summary-row {
        font-size: 1.4rem;
        padding: 10px 0;
    }
    
    .summary-row .label {
        font-size: 1.4rem;
    }
    
    .summary-row .value {
        font-size: 1.5rem;
        font-weight: 700;
    }
    
    .summary-row.total {
        padding-top: 14px;
        padding-bottom: 10px;
    }
    
    .summary-row.total .label,
    .summary-row.total .value {
        font-size: 2rem;
        font-weight: 800;
    }
    
    .order-summary-inline {
        padding: 18px;
        margin-top: 20px;
        border-radius: 12px;
    }
    
    .order-summary-inline h5 {
        font-size: 1.7rem;
        margin-bottom: 14px;
    }
    
    .order-summary-inline .summary-row {
        font-size: 1.4rem;
    }
    
    /* Mobile buttons - Better touch targets */
    .submit,
    .submit-continue,
    .submit-pay {
        width: 100%;
        padding: 16px 24px !important;
        font-size: 1.7rem !important;
        margin-top: 18px;
        border-radius: 12px;
        font-weight: 700;
        letter-spacing: 0.3px;
    }
    
    /* Mobile payment section - Cleaner layout */
    .payment-method-header {
        padding: 16px;
        margin-bottom: 20px;
        border-radius: 10px;
    }
    
    .payment-title-row {
        margin-bottom: 12px;
    }
    
    .payment-title-row h5 {
        font-size: 1.7rem;
    }
    
    .trust-badges {
        flex-direction: row;
        gap: 6px;
        flex-wrap: wrap;
    }
    
    .trust-badge {
        font-size: 1.1rem;
        padding: 5px 10px;
    }
    
    .payment-description {
        font-size: 1.35rem;
        padding: 12px;
        line-height: 1.6;
    }
    
    .accepted-cards-row {
        margin-top: 12px;
    }
    
    .cards-label {
        font-size: 1.25rem;
        margin-bottom: 8px;
    }
    
    .card-logos {
        gap: 8px;
    }
    
    .card-logo {
        font-size: 1.1rem;
        padding: 6px 12px;
        border-radius: 6px;
    }
    
    .security-footer {
        padding: 14px;
        margin-top: 18px;
        border-radius: 10px;
    }
    
    .stripe-powered {
        flex-direction: row;
        align-items: center;
        gap: 10px;
        margin-bottom: 12px;
        padding-bottom: 12px;
    }
    
    .powered-text {
        font-size: 1.2rem;
    }
    
    .security-features {
        gap: 10px;
    }
    
    .security-item {
        padding: 8px 10px;
    }
    
    .security-label {
        font-size: 1.15rem;
    }

    /* Hide any vertical coupon / order summary rails on small screens */
    .card-summary::before,
    .card-summary::after {
        content: none !important;
        display: none !important;
    }
}

/* Extra small devices (phones < 480px) */
@media (max-width: 480px) {
    .container {
        padding: 0 10px;
    }
    
    .form-section {
        padding: 14px;
        border-radius: 12px;
        margin-bottom: 10px;
    }
    
    .section-header {
        padding: 6px 0;
        gap: 8px;
    }
    
    .section-title-row h4 {
        font-size: 1.55rem;
        font-weight: 700;
    }
    
    .section-preview {
        padding: 10px 12px;
    }
    
    .preview-name {
        font-size: 1.35rem;
    }
    
    .preview-address,
    .preview-method {
        font-size: 1.2rem;
    }
    
    .edit-btn {
        padding: 7px 14px;
        font-size: 1.2rem;
    }
    
    .form-control {
        margin-bottom: 12px;
    }
    
    .form-control label {
        font-size: 1.3rem;
        margin-bottom: 6px;
    }
    
    .form-control input,
    .form-control textarea {
        font-size: 1.55rem;
        padding: 13px 12px;
        border-radius: 10px;
    }
    
    .shipping-option {
        padding: 12px;
    }
    
    .shipping-name {
        font-size: 1.4rem;
    }
    
    .shipping-price {
        font-size: 1.6rem;
    }
    
    .shipping-badge {
        font-size: 1.2rem;
        padding: 4px 10px;
    }
    
    .shipping-description {
        padding-left: 28px;
    }
    
    .shipping-time {
        font-size: 1.2rem;
    }
    
    .shipping-desc-text {
        font-size: 1.2rem;
    }
    
    .cart-row {
        padding: 12px;
        margin-bottom: 10px;
    }
    
    .product-cell {
        gap: 10px;
    }
    
    .thumb {
        width: 65px;
        height: 65px;
    }
    
    .product-title {
        font-size: 1.4rem;
        line-height: 1.3;
    }
    
    .chip {
        font-size: 1.05rem;
        padding: 3px 7px;
    }
    
    .qty-cell {
        padding-top: 6px;
    }
    
    .qty-control {
        min-width: 100px;
        padding: 5px 10px;
    }
    
    .qty-btn {
        font-size: 1.7rem;
    }
    
    .qty-value {
        font-size: 1.4rem;
    }
    
    .price-cell {
        font-size: 1.7rem;
    }
    
    .trash {
        top: 10px;
        right: 10px;
        padding: 5px;
    }
    
    aside .card {
        padding: 16px;
        border-radius: 12px;
    }
    
    aside h4 {
        font-size: 1.7rem;
        padding-bottom: 10px;
    }
    
    .coupon-input {
        gap: 6px;
    }
    
    .coupon-input input {
        font-size: 1.45rem;
        padding: 11px 12px;
    }
    
    .coupon-input .apply {
        padding: 11px 18px;
        font-size: 1.35rem;
    }
    
    .summary-row {
        font-size: 1.35rem;
        padding: 8px 0;
    }
    
    .summary-row .label {
        font-size: 1.35rem;
    }
    
    .summary-row .value {
        font-size: 1.45rem;
    }
    
    .summary-row.total {
        padding-top: 12px;
    }
    
    .summary-row.total .label,
    .summary-row.total .value {
        font-size: 1.85rem;
        font-weight: 800;
    }
    
    .order-summary-inline {
        padding: 16px;
        margin-top: 16px;
    }
    
    .order-summary-inline h5 {
        font-size: 1.6rem;
    }
    
    .submit,
    .submit-continue,
    .submit-pay {
        font-size: 1.6rem !important;
        padding: 15px 20px !important;
        margin-top: 16px;
    }
    
    .payment-method-header {
        padding: 14px;
    }
    
    .payment-title-row h5 {
        font-size: 1.6rem;
    }
    
    .trust-badge {
        font-size: 1.05rem;
        padding: 4px 9px;
    }
    
    .payment-description {
        font-size: 1.3rem;
        padding: 10px;
    }
    
    .cards-label {
        font-size: 1.2rem;
    }
    
    .card-logo {
        font-size: 1.05rem;
        padding: 5px 10px;
    }
    
    .security-footer {
        padding: 12px;
    }
    
    .security-label {
        font-size: 1.1rem;
    }
}
</style>